export { analyzePhases, validateMilestoneChange, MILESTONES, type PhaseAnalysis, type RoastPhase } from "./phases";
export { parseImportCsv, suggestImportMapping, normalizeImportTable, type ImportCell, type ImportTable, type CurveImportMapping } from "./import";
export { parseCropsterPdf, MAX_ROAST_PDF_PAGES, MAX_ROAST_PDF_ITEMS, MAX_ROAST_PDF_TEXT, type PdfText, type CropsterPdfCurve } from "./cropster-pdf";

/** Temperature is stored in °C; elapsed time is measured from charge, in ms. */
export type Sample = { elapsedMs: number; beanC: number | null; environmentC: number | null };
export type TemperatureUnit = "F" | "C";
export type Interval = 15 | 30 | 60;
export const MAX_SAMPLES = 20_000;
export const MAX_ELAPSED_MS = 4 * 60 * 60 * 1000;

function finite(value: number, label: string) {
  if (!Number.isFinite(value)) throw new Error(`${label} must be a finite number.`);
}

function positive(value: number, label: string) {
  finite(value, label);
  if (value <= 0) throw new Error(`${label} must be greater than zero.`);
}

function nonnegative(value: number, label: string) {
  finite(value, label);
  if (value < 0) throw new Error(`${label} cannot be negative.`);
}

export function validateSamples(samples: readonly Sample[]): void {
  if (samples.length < 2 || samples.length > MAX_SAMPLES) {
    throw new Error(`A curve needs 2–${MAX_SAMPLES.toLocaleString("en-US")} samples.`);
  }
  let previous = -1;
  for (const sample of samples) {
    finite(sample.elapsedMs, "Elapsed time");
    if (sample.elapsedMs <= previous || sample.elapsedMs > MAX_ELAPSED_MS || sample.elapsedMs < 0) {
      throw new Error("Times must increase strictly, starting at or after zero, within four hours.");
    }
    for (const value of [sample.beanC, sample.environmentC]) {
      if (value !== null) {
        finite(value, "Temperature");
        if (value < -100 || value > 1000) throw new Error("Temperature must be between −100 and 1,000 °C.");
      }
    }
    previous = sample.elapsedMs;
  }
  if (!samples.some(sample => sample.beanC !== null)) throw new Error("The curve has no bean temperature readings.");
}

export function temperature(celsius: number, unit: TemperatureUnit): number {
  return unit === "F" ? celsius * 9 / 5 + 32 : celsius;
}

/** Display interval scales the slope; calculation window independently controls smoothing. */
export function rateOfRise(
  samples: readonly Sample[],
  atMs: number,
  options: { unit: TemperatureUnit; intervalSeconds: Interval; windowSeconds: Interval; maxGapMs: number },
): number | null {
  const startMs = atMs - options.windowSeconds * 1000;
  if (startMs < 0) return null;
  const endIndex = samples.findLastIndex(sample => sample.elapsedMs <= atMs);
  const startIndex = samples.findLastIndex(sample => sample.elapsedMs <= startMs);
  if (startIndex < 0 || endIndex < 0 || endIndex <= startIndex) return null;
  const window = samples.slice(startIndex, endIndex + 1);
  const end = window[window.length - 1]!;
  if (atMs - end.elapsedMs > options.maxGapMs || startMs - window[0]!.elapsedMs > options.maxGapMs) return null;
  if (window.some((sample, index) => sample.beanC === null ||
    (index > 0 && sample.elapsedMs - window[index - 1]!.elapsedMs > options.maxGapMs))) return null;

  // Causal least-squares slope, with a sample at/before the window boundary.
  // Subtract startMs before regression to avoid cancellation on large timestamps.
  const meanX = window.reduce((sum, s) => sum + (s.elapsedMs - startMs) / 1000, 0) / window.length;
  const meanY = window.reduce((sum, s) => sum + s.beanC!, 0) / window.length;
  let numerator = 0;
  let denominator = 0;
  for (const sample of window) {
    const dx = (sample.elapsedMs - startMs) / 1000 - meanX;
    numerator += dx * (sample.beanC! - meanY);
    denominator += dx * dx;
  }
  if (denominator === 0) return null;
  return numerator / denominator * options.intervalSeconds * (options.unit === "F" ? 9 / 5 : 1);
}

/** A deliberately narrow interchange format, not a vendor-format compatibility claim. */
export function parseTelemetryCsv(text: string): Sample[] {
  if (text.length > 2_000_000) throw new Error("CSV must be smaller than 2 MB.");
  const lines = text.replace(/^\uFEFF/, "").trim().split(/\r?\n/);
  if (lines.shift()?.trim() !== "elapsed_seconds,bean_c,environment_c") {
    throw new Error("Use the header elapsed_seconds,bean_c,environment_c. Temperatures must be °C.");
  }
  const samples = lines.map((line, i) => {
    const cells = line.split(",").map(cell => cell.trim());
    if (cells.length !== 3 || cells[0] === "") throw new Error(`Row ${i + 2}: expected three columns and an elapsed time.`);
    const readNumber = (cell: string) => {
      if (!/^-?(?:\d+(?:\.\d*)?|\.\d+)$/.test(cell)) throw new Error(`Row ${i + 2}: use decimal numbers, or a blank temperature for a missing reading.`);
      return Number(cell);
    };
    return {
      elapsedMs: readNumber(cells[0]!) * 1000,
      beanC: cells[1] === "" ? null : readNumber(cells[1]!),
      environmentC: cells[2] === "" ? null : readNumber(cells[2]!),
    };
  });
  validateSamples(samples);
  return samples;
}

export function telemetryCsv(samples: readonly Sample[]): string {
  return "elapsed_seconds,bean_c,environment_c\n" + samples.map(sample =>
    `${sample.elapsedMs / 1000},${sample.beanC === null ? "" : sample.beanC},${sample.environmentC === null ? "" : sample.environmentC}`,
  ).join("\n");
}

/** Net weights only. Expected output must never be posted as measured inventory. */
export function roastYield(greenWeight: number, roastedWeight: number): number {
  positive(greenWeight, "Green weight");
  positive(roastedWeight, "Roasted weight");
  if (roastedWeight > greenWeight) throw new Error("Roasted net weight cannot exceed green net weight. Check tare and units.");
  return roastedWeight / greenWeight;
}

export type YieldObservation = {
  lot: string; profileVersion: string; machine: string; batchBand: string;
  greenWeight: number; roastedWeight: number; measured: boolean; qcPassed: boolean;
};

export function estimateYield(history: readonly YieldObservation[], match: Pick<YieldObservation, "lot" | "profileVersion" | "machine" | "batchBand">): { yield: number; count: number } | null {
  const eligible = history.filter(item => item.measured && item.qcPassed && item.lot === match.lot &&
    item.profileVersion === match.profileVersion && item.machine === match.machine && item.batchBand === match.batchBand);
  if (!eligible.length) return null;
  eligible.forEach(item => roastYield(item.greenWeight, item.roastedWeight));
  return {
    yield: eligible.reduce((sum, item) => sum + item.roastedWeight, 0) / eligible.reduce((sum, item) => sum + item.greenWeight, 0),
    count: eligible.length,
  };
}

export type BlendComponent = {
  lot: string; profileVersion: string; machine: string; batchBand: string;
  fraction: number; yield: number;
};
export type BlendDemand = { name: string; roastedWeight: number; components: BlendComponent[] };
export type PlannedComponent = Omit<BlendComponent, "fraction"> & { roastedWeight: number; greenWeight: number };

/** Post-roast fractions are roasted mass. Aggregation requires the entire roast context to match. */
export function planPostRoastBlends(demands: readonly BlendDemand[]): PlannedComponent[] {
  const groups = new Map<string, PlannedComponent>();
  for (const demand of demands) {
    positive(demand.roastedWeight, `${demand.name} target`);
    if (!demand.components.length) throw new Error(`${demand.name}: add at least one component.`);
    const total = demand.components.reduce((sum, component) => sum + component.fraction, 0);
    if (!Number.isFinite(total) || Math.abs(total - 1) > 0.000001) {
      throw new Error(`${demand.name} recipe totals ${(total * 100).toFixed(1)}%. Specify all components; it must total 100%.`);
    }
    for (const component of demand.components) {
      for (const [label, value] of Object.entries({lot: component.lot, profile: component.profileVersion, machine: component.machine, batchBand: component.batchBand})) {
        if (!value.trim()) throw new Error(`Every component needs a ${label}.`);
      }
      positive(component.fraction, "Blend fraction");
      positive(component.yield, "Expected yield");
      if (component.yield > 1) throw new Error("Expected yield cannot exceed 100%.");
      const key = JSON.stringify([component.lot, component.profileVersion, component.machine, component.batchBand]);
      const roastedWeight = demand.roastedWeight * component.fraction;
      const existing = groups.get(key);
      if (existing && existing.yield !== component.yield) throw new Error("Matching roast contexts have conflicting yield assumptions.");
      if (existing) {
        existing.roastedWeight += roastedWeight;
        existing.greenWeight += roastedWeight / component.yield;
      } else {
        groups.set(key, {...component, roastedWeight, greenWeight: roastedWeight / component.yield});
      }
    }
  }
  return [...groups.values()];
}

/** Pre-roast fractions are green mass; the blend has its own measured yield. */
export function planPreRoastBlend(roastedWeight: number, expectedYield: number, fractions: readonly number[]): number[] {
  positive(roastedWeight, "Roasted target");
  positive(expectedYield, "Expected yield");
  if (expectedYield > 1) throw new Error("Expected yield cannot exceed 100%.");
  if (fractions.length === 0 || Math.abs(fractions.reduce((sum, fraction) => sum + fraction, 0) - 1) > 0.000001) {
    throw new Error("Pre-roast green fractions must total 100%.");
  }
  fractions.forEach(fraction => positive(fraction, "Green fraction"));
  return fractions.map(fraction => roastedWeight / expectedYield * fraction);
}

export function packagingCost(input: {
  greenWeight: number; roastedWeight: number; sampleWeight: number; wasteWeight: number;
  packWeight: number; packs: number; greenCostPerWeight: number;
  roastLaborDollars: number; packagingLaborDollars: number; energyDollars: number;
  packagingDollarsPerPack: number; retailDollarsPerPack: number;
}): { availableWeight: number; remainingLooseWeight: number; costPerPack: number; marginPercent: number } {
  roastYield(input.greenWeight, input.roastedWeight);
  positive(input.packWeight, "Pack weight");
  if (!Number.isSafeInteger(input.packs) || input.packs < 1) throw new Error("Pack count must be a positive whole number.");
  for (const [label, value] of Object.entries(input)) nonnegative(value, label);
  positive(input.retailDollarsPerPack, "Retail price");
  const availableWeight = input.roastedWeight - input.sampleWeight - input.wasteWeight;
  positive(availableWeight, "Saleable roasted weight");
  const packedWeight = input.packWeight * input.packs;
  if (packedWeight > availableWeight + 1e-9) throw new Error("Packaging exceeds the available roast after QC samples and waste.");
  const coffeeCostPerWeight = (input.greenWeight * input.greenCostPerWeight + input.roastLaborDollars + input.energyDollars) / availableWeight;
  const costPerPack = coffeeCostPerWeight * input.packWeight + input.packagingDollarsPerPack + input.packagingLaborDollars / input.packs;
  return { availableWeight, remainingLooseWeight: availableWeight - packedWeight, costPerPack,
    marginPercent: (input.retailDollarsPerPack - costPerPack) / input.retailDollarsPerPack * 100 };
}
